import React, { useCallback, useState } from 'react';
import { Search, AlertCircle } from 'lucide-react';
import { useStore } from '../store';
import { GPTResponse } from '../types';

interface SearchBarProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
}

export const SearchBar: React.FC<SearchBarProps> = ({ 
  value: externalValue,
  onChange: externalOnChange,
  placeholder = 'מתי כדאי...?'
}) => {
  const { query, setQuery, setLoading, setAiResponse, topics } = useStore();
  const [error, setError] = useState<string | null>(null);
  const [showResults, setShowResults] = useState(false);

  const value = typeof externalValue !== 'undefined' ? externalValue : query;
  const setValue = externalOnChange || setQuery;

  const filteredTopics = topics.filter(topic =>
    topic.question.includes(value.trim())
  );

  const askGPT = useCallback(async () => {
    if (!query.trim()) return;

    try {
      setLoading(true);
      setError(null);
      setAiResponse(null);

      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 15000);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ask-gpt`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ question: query.trim() }),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to get response');
      }

      const data: GPTResponse = await response.json();

      if (!data.bestTime || !data.explanation) {
        throw new Error('Invalid response format');
      }

      setAiResponse({
        id: 'ai-response',
        question: query.trim(),
        bestTime: data.bestTime,
        explanation: data.explanation,
        tip: data.tip,
      });
    } catch (error: any) {
      console.error('Error fetching AI response:', error);
      if (error.name === 'AbortError') {
        setError('התשובה לוקחת יותר מדי זמן. נסו שוב או נסחו את השאלה אחרת.');
      } else {
        setError('לא הצלחנו לקבל תשובה כרגע. אנא נסו שוב מאוחר יותר.');
      }
    } finally {
      setLoading(false);
    }
  }, [query, setLoading, setAiResponse]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!externalOnChange) {
      askGPT();
    }
    setShowResults(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setValue(newValue);
    setShowResults(newValue.trim().length > 0);
  };

  return (
    <div className="relative w-full max-w-2xl">
      <form onSubmit={handleSubmit} className="relative">
        <input
          type="text"
          value={value}
          onChange={handleInputChange}
          onBlur={() => setTimeout(() => setShowResults(false), 200)}
          placeholder={placeholder}
          className="w-full px-6 py-4 text-xl text-right rounded-full border-2 border-blue-200 focus:border-blue-400 focus:outline-none shadow-lg pr-14"
          dir="rtl"
        />
        <button
          type="submit"
          className="absolute right-4 top-1/2 transform -translate-y-1/2 text-blue-400 hover:text-blue-600 transition-colors"
        >
          <Search className="w-6 h-6" />
        </button>
      </form>

      {showResults && filteredTopics.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-2 bg-white rounded-lg shadow-xl border border-gray-200 max-h-96 overflow-y-auto z-50">
          {filteredTopics.map(topic => (
            <button
              key={topic.id}
              onClick={() => {
                setValue(topic.question);
                setShowResults(false);
                if (!externalOnChange) {
                  setAiResponse(topic);
                }
              }}
              className="w-full text-right px-4 py-3 hover:bg-gray-50 border-b last:border-b-0"
            >
              <div className="font-medium text-gray-800">
                מתי כדאי {topic.question}?
              </div>
              <div className="text-sm text-gray-500 mt-1">
                {topic.bestTime}
              </div>
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="mt-4 p-4 bg-red-50 text-red-700 rounded-lg flex items-center gap-2 text-right">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}
    </div>
  );
};
